import React from "react";
import { ENTRIES } from "../shared/assets/entry_data";
import { PAYOUTS } from "../shared/assets/payouts";
import { useLeaderboard } from "../shared/assets/useLeaderboard";
import { useIsFinal } from "../context/IsFinalContext";

function comparator(a, b) {
    if (a.status === "cut" && b.status !== "cut") return 1;
    if (a.status !== "cut" && b.status === "cut") return -1;
    if (a.position !== b.position) return a.position - b.position;
    return a.toPar - b.toPar;
}

function scoreClass(score) {
    if (score === null || score === undefined || score === "-") return "";
    const num = typeof score === "string" ? parseFloat(score) : score;
    return num < 0 ? "text-red-600" : "";
}

function Golfers() {
    const { golfers, lastUpdated, currentRound, loading, error } = useLeaderboard();
    const { isFinal } = useIsFinal();
    const [selectedGolfer, setSelectedGolfer] = React.useState(null);

    if (loading) return <p className="mt-20 text-center">Loading leaderboard...</p>;
    if (error) return <p className="mt-20 text-center text-red-600">{error}</p>;

    var formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
    });

    const sortedGolfers = [...golfers].sort(comparator).map((golfer) => {
        // Position display
        let position = "";
        if (golfer.status === "cut") {
            position = "CUT";
        } else if (golfer.status === "wd") {
            position = "WD";
        } else {
            position = (golfer.posQualifier ?? "") + (golfer.position ?? "");
        }

        // Score display
        let curScore = "";
        if (golfer.toPar === null || golfer.toPar === undefined) {
            curScore = "-";
        } else if (golfer.toPar > 0) {
            curScore = "+" + golfer.toPar;
        } else if (golfer.toPar === 0) {
            curScore = "E";
        } else {
            curScore = golfer.toPar;
        }

        // Today's score display
        let today = "-";
        let todayToPar = null;
        if (currentRound && golfer.toPar !== null && golfer.toPar !== undefined) {
            const roundScores = [golfer.round1, golfer.round2, golfer.round3, golfer.round4];
            // Sum up all completed previous rounds' to-par scores
            let previousToPar = 0;
            for (let i = 0; i < currentRound - 1; i++) {
                const r = roundScores[i];
                if (r && r !== 0) {
                    previousToPar += r - 72;
                }
            }
            todayToPar = golfer.toPar - previousToPar;
            if (todayToPar > 0) today = "+" + todayToPar;
            else if (todayToPar === 0) today = "E";
            else today = todayToPar;
        }

        // Thru display
        let thru = (golfer.thru === 0 || golfer.thru === null || golfer.thru === undefined) ? "--" : golfer.thru;

        // Winnings display
        let dollars = "";
        if (golfer.status === "cut") {
            dollars = formatter.format(25000);
        } else if (isFinal) {
            dollars = formatter.format(golfer.winnings ?? 0);
        } else if (golfer.position >= 1) {
            dollars = formatter.format(PAYOUTS[golfer.position - 1] ?? 0);
        } else {
            dollars = "-";
        }

        return (
            <div className="grid grid-cols-10 m-1 bg-grey-200" key={golfer.name}>
                <span className="col-span-1">{position}</span>
                <span className={`col-span-1 ${scoreClass(golfer.toPar)}`}>{curScore}</span>
                <span className={`col-span-1 ${scoreClass(todayToPar)}`}>{today}</span>
                <span className="col-span-1 text-sm">{thru}</span>
                <span className="place-self-start col-span-2 cursor-pointer hover:text-blue-600" onClick={() => setSelectedGolfer(golfer)}>{golfer.name}</span>
                <span className="col-span-2">
                    {[golfer.round1, golfer.round2, golfer.round3, golfer.round4]
                        .map(r => r ?? "-")
                        .join(" - ")}
                </span>
                <span className="col-span-2">{dollars}</span>
            </div>
        );
    });

    return (
        <div className="flex flex-col lg:w-7/12 w-100 items-center xbg-pink-200">
            <div className="bg-gray-300 mt-14 rounded-lg pb-4">
                <p className="text-3xl my-6 bg-masters-green masters-label">Player Leaderboard</p>
                <div className="grid grid-cols-10 m-1 font-bold">
                    <span className="col-span-1">Pos</span>
                    <span className="col-span-1">Score</span>
                    <span className="col-span-1">Today</span>
                    <span className="col-span-1">Thru</span>
                    <span className="place-self-start col-span-2">Golfer</span>
                    <span className="col-span-2">Rounds</span>
                    <span className="col-span-2">Winnings</span>
                </div>
                {sortedGolfers}
            </div>
            {lastUpdated && (
                <p className="text-center text-sm text-gray-500 mt-2 mb-4">
                    Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                </p>
            )}
            
            {selectedGolfer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedGolfer(null)}>
                    <div className="bg-white rounded-lg shadow-2xl p-6 w-96 max-h-96 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold">{selectedGolfer.name}</h2>
                            <button onClick={() => setSelectedGolfer(null)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
                        </div>
                        <div className="text-base">
                            <p className="font-semibold mb-3">Appears on entries:</p>
                            <ul className="ml-4">
                                {ENTRIES.filter(entry => entry.golfers.includes(selectedGolfer.name)).map((entry) => (
                                    <li key={entry.name} className="mb-2 text-gray-700">{entry.name}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Golfers;